import { Modal, TextField } from "@mui/material";
import { FC, useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  DropdownInputByControl,
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeader,
  MuiButton,
  MuiSwitch,
} from "@ryde/controls";
import { useYupValidationResolver } from "@ryde/redux";
import { resolveScope, useViewScopePermission, ViewDto } from "@ryde/shared";

import { useDashboardViews } from "../../hooks/use-dashboard-views";
import { dashboardViewEditValidation } from "./dashboard-view-edit-validation";
import { IDashboardViewEditFormValues } from "./dashboard-view-edit.model";
import { DashboardViewEditDialogContainer } from "./dashboard-view-edit.styled";
import { DashboardViewContext } from "./dashboard-view.context";

interface IDialogViewEditProps {
  onClose: () => void;
  view?: ViewDto;
}

export const DashboardDialogViewEdit: FC<IDialogViewEditProps> = ({
  view,
  onClose,
}) => {
  const { t } = useTranslation();
  const { scopeOptions, isSaveButtonDisabled } = useViewScopePermission();
  const { generateName, saveNewView, updateViewMeta } = useDashboardViews();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolver = useYupValidationResolver(dashboardViewEditValidation);
  const isEdit = Boolean(view);
  const { setIsDropdownOpen } = useContext(DashboardViewContext);
  const methods = useForm<IDashboardViewEditFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      name: isEdit ? view?.name : generateName(),
      isDefault: isEdit ? view?.isDefault : false,
      scope: isEdit ? resolveScope(view as ViewDto) : scopeOptions.at(0),
    },
    resolver,
  });
  const isSaveButtonDisabledResolved =
    isSaveButtonDisabled || !methods.formState.isValid || isSubmitting;
  const isDefaultWatch = methods.watch("isDefault", false);
  const title = isEdit ? t("view.update") : t("view.create");

  const onAccept = async (): Promise<void> => {
    if (!methods.formState.isValid) {
      return;
    }

    const values = methods.getValues();

    setIsSubmitting(true);
    try {
      if (view) {
        await updateViewMeta(Number(view.id), {
          name: values.name,
          scope: values.scope,
          isDefault: values.isDefault,
        });
        setIsDropdownOpen?.(false);
      } else {
        await saveNewView(values.name, values.scope, values.isDefault);
      }

      onClose();
    } catch {
      // Keep the dialog open so the user can retry (the API layer toasts the error).
      setIsSubmitting(false);
    }
  };

  const onCancel = (): void => {
    onClose();
  };

  return (
    <Modal open onClose={onClose}>
      <ModalContainerStyled>
        <ModalHeader onClose={onClose}>{title}</ModalHeader>
        <ModalContentStyled>
          <DashboardViewEditDialogContainer>
            <FormProvider {...methods}>
              <TextField
                label={`${t(`view.formLabel.name`)}`}
                fullWidth
                data-e2e="view-name-modal-input"
                required
                {...methods.register("name")}
              />
              <DropdownInputByControl
                name="scope"
                formLabel={t("view.scopeLabel")}
                control={methods.control}
                formValidation={{
                  type: "string",
                  rules: [{ validation: "required" }],
                }}
                formAttributes={{
                  selectItems: scopeOptions.map(scopeItem => ({
                    value: scopeItem,
                    label: t(`view.${scopeItem}`),
                  })),
                }}
              />
              <MuiSwitch
                onChange={e => methods.setValue("isDefault", e.target.checked)}
                checked={isDefaultWatch}
                label={t(`view.formLabel.default`)}
                e2eData="save-view-switch"
              />
            </FormProvider>
          </DashboardViewEditDialogContainer>
        </ModalContentStyled>
        <ModalFooterStyled>
          <MuiButton
            muiStyle="primary"
            muiVariant="outlined"
            onClick={onCancel}
            e2eData="cancel-modal-button"
          >
            {t(`general.CANCEL`)}
          </MuiButton>
          <MuiButton
            disabled={isSaveButtonDisabledResolved}
            onClick={onAccept}
            e2eData="ok-modal-button"
          >
            {t(`general.SAVE`)}
          </MuiButton>
        </ModalFooterStyled>
      </ModalContainerStyled>
    </Modal>
  );
};
